import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import { Appointment, Patient, UserFeedback } from 'generated/api';
import './style.scss';
import { PhysicianImage } from 'components/Common/UserImage';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import DefaultButton from 'components/Utils/NewDefaultButton';
import { api } from 'careand-redux/api/ApiThunks';
import { useIsLoading } from 'careand-redux/slices/ui/hooks';
import PhysicianUtils from 'services/PhysicianUtils';
import AppointmentUtils from 'services/AppointmentUtils';
import Icon from 'components/Utils/NewIcon';
import CalendarUtils from 'services/CalendarUtils';
import Rating from '@material-ui/lab/Rating';
import TextareaAutosize from 'components/Common/TextAreaAutosize';
import PatientUtils from 'services/PatientUtils';
import moment from 'moment-timezone';
import DefaultPopup from 'components/Utils/DefaultPopup';
import MiscUtils from 'services/MiscUtils';

interface RateAppointmentProps {
  appointment: Appointment;
  onSuccess?: () => void;
  showInfo?: boolean;
}

const RateAppointment: FC<RateAppointmentProps> = ({ appointment, onSuccess, showInfo = true }) => {
  const dispatch = useAppDispatch();
  const loading = useIsLoading(api.Appointments.rateAppointment);
  const loadingComment = useIsLoading(api.Resource.sendUserFeedback);
  const physician = useAppSelector((state) => state.physicians.byId[appointment.physician_id!]);
  const patient = useAppSelector((state) => state.patients.byId[appointment.patient_id!]);
  const clinics = useAppSelector((state) => state.resource.clinics);

  const [rating, setRating] = useState<number | undefined>(appointment.rating);
  const [showComments, setShowComments] = useState<boolean>(false);
  const [showGoogleReview, setShowGoogleReview] = useState<boolean>(false);
  const [comment, setComment] = useState<string>('');

  let clinic = clinics.find(({ id }) => id === appointment.clinic_id);

  if (appointment.status !== AppointmentUtils.getAppointmentStatus().completed) return null;

  async function onSubmit() {
    if (!rating) return;
    let shouldAskToReview = true;
    let result = await dispatch(api.Appointments.rateAppointment({ id: appointment.id!, rating }), {
      errorTitle: 'Appointment Rating',
    });
    if (result) {
      if (!appointment.read) {
        dispatch(
          api.HealthRecords.markHealthRecordRead({
            patientId: appointment.patient_id!,
            type: 'Appointment',
            id: appointment.id!,
          }),
        );
      }
      if (rating <= 3) {
        setShowComments(true);
      } else {
        if (shouldAskToReview) {
          setShowGoogleReview(true);
        } else {
          if (onSuccess) onSuccess();
        }
      }
    }
  }

  async function submitComment() {
    if (!comment) return;
    let userFeedback: UserFeedback = {
      rating,
      text: comment,
      physician_id: appointment.physician_id,
      patient_id: appointment.patient_id,
      name: PatientUtils.getName(patient),
      clinic_id: appointment.clinic_id,
      appointment_id: appointment.id,
    };
    let result = await dispatch(api.Resource.sendUserFeedback({ userFeedback }), {
      title: 'Submit Feedback',
      successText: 'Thank you for submitting your feedback!',
    });
    if (result && onSuccess) onSuccess();
    else if (result) {
      setShowComments(false);
    }
  }

  function goToGoogleReview() {
    MiscUtils.openExternalLink(`${(clinic || clinics[0])?.google_url}/review?gm`);
  }

  const alreadyRated = appointment.rating !== undefined && appointment.rating > 0;

  return (
    <div className="rate-appointment">
      {showInfo && (
        <>
          <span className="description">How did your appointment go?</span>
          <PhysicianImage user={physician} size={80} />
          <span className="name">{PhysicianUtils.getName(physician)}</span>
          <span className="type">{AppointmentUtils.getAppointmentDescription(appointment)}</span>
          <div className="date">
            <Icon src="calendar" width="16px" />
            <span>{CalendarUtils.format(appointment.date!, 'DD MMMM YYYY')}</span>
            <Icon src="clock" width="16px" />
            <span>{CalendarUtils.format(appointment.date!, 'h:mm a')}</span>
          </div>
        </>
      )}

      {!showGoogleReview && (
        <Rating
          disabled={loading || alreadyRated}
          value={rating}
          onChange={(event, newValue) => setRating(newValue ?? undefined)}
          emptyIcon={<Icon src="star" className="empty" width="45px" height="45px" />}
          icon={
            <Icon src="star" className="filled" fontAwesomeStyle="fas" width="45px" height="45px" />
          }
        />
      )}
      {!showGoogleReview && !showComments && (
        <DefaultButton
          name={`Rate${rating ? ` ${rating} stars` : ''}`}
          onClick={onSubmit}
          disabled={!rating || alreadyRated}
          loading={loading}
          hide={alreadyRated}
        />
      )}
      {showComments && (
        <div className="comments">
          <span className="description">Comments</span>
          <TextareaAutosize
            cacheMeasurements
            name="comment"
            placeholder="Please let us know how we can improve"
            value={comment}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setComment(event.target.value)}
          />
          <DefaultButton
            name="Submit Comment"
            onClick={submitComment}
            loading={loadingComment}
            disabled={!comment}
          />
        </div>
      )}

      {showGoogleReview && (
        <>
          <span className="text-center google-large">Thank you for your review!</span>
          <span className="text-center google">
            It would help us a lot if you could rate us on Google!
          </span>
          <DefaultButton name="Go to Google Review Page" onClick={goToGoogleReview} />
        </>
      )}
    </div>
  );
};

interface AppointmentRatingWidgetProps {
  patient: Patient;
}

export const AppointmentRatingWidget: FC<AppointmentRatingWidgetProps> = ({ patient }) => {
  const allPhysicians = useAppSelector((state) => state.physicians.byId);
  const patientAppointments = useAppSelector((state) => state.appointments.byId[patient.id!]);
  const [showPopup, setShowPopup] = useState<boolean>(false);

  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | undefined>(
    undefined,
  );

  const ratingCutoff = moment().subtract(1, 'month').unix();

  const waitingRating =
    patientAppointments?.filter(
      ({ rating, date, status }) =>
        status === AppointmentUtils.getAppointmentStatus().completed &&
        !rating &&
        date! >= ratingCutoff,
    ) || [];

  let appointment = waitingRating.reverse()[0];

  useEffect(() => {
    if (appointment) {
      setSelectedAppointment(appointment);
    }
  }, [appointment]);

  if (!selectedAppointment) return null;

  const physician = allPhysicians[selectedAppointment?.physician_id!];

  return (
    <div className="appointment-rating-widget widget-box-wrapper">
      <span className="box-title">How was your appointment?</span>
      <div className="box">
        <div className="content">
          <div className="badge">
            <PhysicianImage user={physician} size={70} />
            <div className="info">
              <span className="name truncate">{PhysicianUtils.getName(physician)}</span>
              <span className="date">
                {CalendarUtils.format(selectedAppointment.date!, 'MMM DD, YYYY [at] h:mm a')}
              </span>
            </div>
          </div>
          <DefaultButton
            name="Rate Appointment"
            onClick={() => setShowPopup(true)}
            icon={['fas', 'star']}
          />
        </div>
      </div>
      <DefaultPopup
        title="Rate Appointment"
        className="appointment-rating-popup"
        openHandler={[
          showPopup,
          (value) => {
            setShowPopup(value);
            if (!value && !appointment) {
              setSelectedAppointment(undefined);
            }
          },
        ]}
      >
        <RateAppointment
          appointment={selectedAppointment}
          onSuccess={() => {
            setSelectedAppointment(undefined);
            setShowPopup(false);
          }}
        />
      </DefaultPopup>
    </div>
  );
};

export default RateAppointment;
